import React, { useState, useEffect } from 'react';
import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Modular Filter Module Component
 * 
 * A comprehensive, reusable filter component that can be used across different parts of the app.
 * Supports multiple filter types: multiselect, boolean, numerical, date, and custom filters.
 * 
 * @param {Object} props
 * @param {Array} props.filters - Array of filter objects
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Object} props.config - Configuration object for filter fields and options
 * @param {Boolean} props.collapsible - Whether the filter section is collapsible
 * @param {Boolean} props.defaultExpanded - Whether the filter section is expanded by default
 * @param {String} props.title - Title of the filter section
 * @param {Function} props.onAddFilter - Callback when "Add Filter" is clicked
 * @param {Function} props.getFieldOptions - Function to get options for a field
 */
const FilterModule = ({
  filters = [],
  onFiltersChange,
  config = {},
  collapsible = true,
  defaultExpanded = true,
  title = 'FILTERS',
  onAddFilter,
  getFieldOptions = () => [],
  noContainer = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const handleFilterChange = (filterId, updates) => {
    const updatedFilters = filters.map(filter => 
      filter.id === filterId ? { ...filter, ...updates } : filter
    );
    onFiltersChange(updatedFilters);
  };

  const handleRemoveFilter = (filterId) => {
    const updatedFilters = filters.filter(filter => filter.id !== filterId);
    onFiltersChange(updatedFilters);
  };

  const handleAddFilter = () => {
    if (onAddFilter) {
      onAddFilter();
    }
  };

  const containerClasses = noContainer 
    ? "bg-transparent overflow-hidden" 
    : "bg-transparent border border-dotted border-gray-300 rounded-lg overflow-hidden";

  return (
    <div className={containerClasses} style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Header */}
      <div className={`pl-4 pr-6 pt-2 pb-3 flex items-center justify-between ${noContainer ? '' : 'border-b border-dotted border-gray-300'}`}>
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
          {title}
        </h3>
        {collapsible && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-[#475569]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#475569]" />
            )}
          </button>
        )}
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="p-6">
          {filters.length > 0 && (
            <div className="space-y-4">
              {filters.map((filter) => (
                <FilterItem
                  key={filter.id}
                  filter={filter}
                  config={config}
                  onFilterChange={(updates) => handleFilterChange(filter.id, updates)}
                  onRemove={() => handleRemoveFilter(filter.id)}
                  getFieldOptions={getFieldOptions}
                />
              ))}
            </div>
          )}

          {/* Add Filter Button */}
          <div className={filters.length > 0 ? 'mt-4' : ''}>
            <button
              onClick={handleAddFilter}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Individual Filter Item Component
 */
const FilterItem = ({ filter, config, onFilterChange, onRemove, getFieldOptions }) => {
  const { field, operator, value, value2 } = filter;
  const [fieldOptions, setFieldOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  const fieldConfig = config.fields?.find(f => f.id === field);

  // Load field options when field changes
  useEffect(() => {
    const loadOptions = async () => {
      if (!field || !fieldConfig) {
        setFieldOptions([]);
        return;
      }

      // Use static options if available
      if (fieldConfig.options) {
        setFieldOptions(fieldConfig.options);
        return;
      }

      // Load dynamic options
      if (fieldConfig.type === 'multiselect') {
        setLoadingOptions(true);
        try {
          const options = await getFieldOptions(field);
          setFieldOptions(Array.isArray(options) ? options : []);
        } catch (error) {
          console.error('Error loading field options:', error);
          setFieldOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      } else {
        setFieldOptions([]);
      }
    };

    loadOptions();
  }, [field, fieldConfig, getFieldOptions]);

  const handleFieldChange = (newField) => {
    const newFieldConfig = config.fields?.find(f => f.id === newField);
    // Reset operator and value when field changes
    onFilterChange({
      field: newField,
      operator: newFieldConfig?.defaultOperator || config.defaultOperators?.[newFieldConfig?.type] || 'equals',
      value: null,
      value2: null
    });
  };

  const handleOperatorChange = (newOperator) => {
    onFilterChange({ operator: newOperator });
  };

  const handleValueChange = (newValue) => {
    onFilterChange({ value: newValue });
  };

  const handleValue2Change = (newValue2) => {
    onFilterChange({ value2: newValue2 });
  };

  if (!fieldConfig) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 p-4 bg-transparent rounded-lg border border-dotted border-gray-300">
      {/* Drag Handle (for future reordering) */}
      <div className="mt-2 cursor-move">
        <div className="w-1 h-6 bg-gray-300 rounded"></div>
        <div className="w-1 h-6 bg-gray-300 rounded mt-1"></div>
      </div>

      <div className="flex-1 grid grid-cols-12 gap-3">
        {/* Field Selector */}
        <div className="col-span-3">
          <label className="block text-xs font-medium text-[#475569] mb-1">Field</label>
          <select
            value={field || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          >
            <option value="">Select field...</option>
            {config.fields?.map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Operator Selector */}
        <div className="col-span-2">
          <label className="block text-xs font-medium text-[#475569] mb-1">Operator</label>
          <select
            value={operator || ''}
            onChange={(e) => handleOperatorChange(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          >
            {config.operators?.[fieldConfig.type]?.map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>
        </div>

        {/* Value Input(s) */}
        <div className={operator === 'between' ? 'col-span-3' : 'col-span-5'}>
          <label className="block text-xs font-medium text-[#475569] mb-1">Value</label>
          {fieldConfig.type === 'multiselect' ? (
            <MultiselectInput
              value={value || []}
              options={fieldOptions}
              onChange={handleValueChange}
              placeholder={loadingOptions ? "Loading..." : "Select values..."}
            />
          ) : fieldConfig.type === 'boolean' ? (
            <select
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          ) : fieldConfig.type === 'date' || fieldConfig.type === 'datetime' ? (
            <input
              type={fieldConfig.type === 'date' ? 'date' : 'datetime-local'}
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            />
          ) : fieldConfig.type === 'number' ? (
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Enter value..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            />
          ) : (
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Enter value..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            />
          )}
        </div>

        {/* Second Value Input (for 'between' operator) */}
        {operator === 'between' && (
          <div className="col-span-3">
            <label className="block text-xs font-medium text-[#475569] mb-1">To</label>
            {fieldConfig.type === 'date' || fieldConfig.type === 'datetime' ? (
              <input
                type={fieldConfig.type === 'date' ? 'date' : 'datetime-local'}
                value={value2 || ''}
                onChange={(e) => handleValue2Change(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            ) : (
              <input
                type={fieldConfig.type === 'number' ? 'number' : 'text'}
                value={value2 || ''}
                onChange={(e) => handleValue2Change(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              />
            )}
          </div>
        )}

        {/* Remove Button */}
        <div className="col-span-1 flex items-end">
          <button
            onClick={onRemove}
            className="p-2 text-[#475569] hover:text-red-600 transition-colors"
            aria-label="Remove filter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Multiselect Input Component
 */
const MultiselectInput = ({ value = [], options = [], onChange, placeholder = "Select values..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(opt => 
    opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-[#1d0c46] flex items-center justify-between"
      >
        <span className={value.length === 0 ? 'text-[#475569]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#475569] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-[#475569]">No options found</div>
              ) : (
                filteredOptions.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">{option.label || option.value}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterModule;
